import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './DaypartConfiguration.css';

interface Daypart {
  key: string;
  name: string;
  startTime: string;
  endTime: string;
  enabled: boolean;
  color: string;
  description?: string;
}

interface DaypartConfiguration {
  enterpriseId: string;
  dayparts: { [key: string]: Daypart };
  timezone: string;
  createdAt: string;
  updatedAt: string;
}

interface DaypartConfigurationProps {
  enterpriseId: string;
  onSave?: (config: DaypartConfiguration) => void;
}

const DaypartConfiguration: React.FC<DaypartConfigurationProps> = ({ 
  enterpriseId, 
  onSave 
}) => {
  const { getIdToken } = useAuth();
  const [configuration, setConfiguration] = useState<DaypartConfiguration | null>(null);
  const [dayparts, setDayparts] = useState<Daypart[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // Predefined templates for different restaurant types
  const templates = {
    'quick-service': {
      name: 'Quick Service Restaurant',
      dayparts: [
        { key: 'BREAKFAST', name: 'Breakfast', startTime: '06:00', endTime: '11:00', enabled: true, color: '#FFE066' },
        { key: 'LUNCH', name: 'Lunch', startTime: '11:00', endTime: '16:00', enabled: true, color: '#66B3FF' },
        { key: 'DINNER', name: 'Dinner', startTime: '16:00', endTime: '22:00', enabled: true, color: '#FF6B66' },
        { key: 'LATE_NIGHT', name: 'Late Night', startTime: '22:00', endTime: '06:00', enabled: true, color: '#B366FF' }
      ]
    },
    'fine-dining': {
      name: 'Fine Dining',
      dayparts: [
        { key: 'LUNCH', name: 'Lunch Service', startTime: '11:30', endTime: '15:00', enabled: true, color: '#66B3FF' },
        { key: 'DINNER', name: 'Dinner Service', startTime: '17:00', endTime: '22:00', enabled: true, color: '#FF6B66' }
      ]
    },
    'coffee-shop': {
      name: 'Coffee Shop / Café',
      dayparts: [
        { key: 'MORNING', name: 'Morning Rush', startTime: '06:00', endTime: '11:00', enabled: true, color: '#8B4513' },
        { key: 'AFTERNOON', name: 'Afternoon', startTime: '11:00', endTime: '17:00', enabled: true, color: '#DEB887' },
        { key: 'EVENING', name: 'Evening', startTime: '17:00', endTime: '21:00', enabled: true, color: '#654321' }
      ]
    },
    'bar-pub': {
      name: 'Bar / Pub',
      dayparts: [
        { key: 'HAPPY_HOUR', name: 'Happy Hour', startTime: '15:00', endTime: '19:00', enabled: true, color: '#FFD700' },
        { key: 'EVENING', name: 'Evening', startTime: '19:00', endTime: '23:00', enabled: true, color: '#FF6B66' },
        { key: 'LATE_NIGHT', name: 'Late Night', startTime: '23:00', endTime: '02:00', enabled: true, color: '#B366FF' }
      ]
    },
    'twenty-four-hour': {
      name: '24-Hour Restaurant',
      dayparts: [
        { key: 'EARLY_MORNING', name: 'Early Morning', startTime: '00:00', endTime: '06:00', enabled: true, color: '#4B0082' },
        { key: 'MORNING', name: 'Morning', startTime: '06:00', endTime: '11:00', enabled: true, color: '#FFE066' },
        { key: 'AFTERNOON', name: 'Afternoon', startTime: '11:00', endTime: '17:00', enabled: true, color: '#66B3FF' },
        { key: 'EVENING', name: 'Evening', startTime: '17:00', endTime: '22:00', enabled: true, color: '#FF6B66' },
        { key: 'NIGHT', name: 'Night', startTime: '22:00', endTime: '00:00', enabled: true, color: '#B366FF' }
      ]
    }
  };

  useEffect(() => {
    loadDaypartConfiguration();
  }, [enterpriseId]);

  const loadDaypartConfiguration = async () => {
    if (!enterpriseId) return;
    
    setLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const response = await fetch(
        API_ENDPOINTS.enterpriseDayparts(enterpriseId),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setConfiguration(data.data);
          setDayparts(Object.values(data.data.dayparts));
        } else {
          // No configuration exists yet - use default
          loadDefaultTemplate('quick-service');
        }
      } else {
        // API endpoint might not exist yet - use default
        loadDefaultTemplate('quick-service');
      }
    } catch (err) {
      console.error('Error loading daypart configuration:', err);
      // Fallback to default template
      loadDefaultTemplate('quick-service');
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultTemplate = (templateKey: string) => {
    const template = templates[templateKey as keyof typeof templates];
    if (template) {
      const defaultDayparts = template.dayparts.map(dp => ({
        ...dp,
        description: `${dp.name} operating period`
      }));
      setDayparts(defaultDayparts);
      setSelectedTemplate(templateKey);
    }
  };

  const loadTemplate = (templateKey: string) => {
    const template = templates[templateKey as keyof typeof templates];
    if (template) {
      setDayparts(template.dayparts.map(dp => ({
        ...dp,
        description: `${dp.name} operating period`
      })));
      setSelectedTemplate(templateKey);
    }
  };

  const addCustomDaypart = () => {
    const newDaypart: Daypart = {
      key: `CUSTOM_${Date.now()}`,
      name: 'Custom Period',
      startTime: '12:00',
      endTime: '18:00',
      enabled: true,
      color: '#999999',
      description: 'Custom daypart period'
    };
    setDayparts([...dayparts, newDaypart]);
  };

  const updateDaypart = (index: number, field: keyof Daypart, value: any) => {
    const updatedDayparts = [...dayparts];
    updatedDayparts[index] = {
      ...updatedDayparts[index],
      [field]: value
    };
    setDayparts(updatedDayparts);
  };

  const removeDaypart = (index: number) => {
    const updatedDayparts = dayparts.filter((_, i) => i !== index);
    setDayparts(updatedDayparts);
  };

  const validateDayparts = (): string | null => {
    if (dayparts.length === 0) {
      return 'At least one daypart is required';
    }

    for (let i = 0; i < dayparts.length; i++) {
      const daypart = dayparts[i];
      
      if (!daypart.name.trim()) {
        return `Daypart ${i + 1}: Name is required`;
      }
      
      if (!daypart.startTime || !daypart.endTime) {
        return `Daypart ${i + 1}: Start and end times are required`;
      }

      // Check for overlaps (simplified - could be more sophisticated)
      for (let j = i + 1; j < dayparts.length; j++) {
        if (dayparts[j].enabled && daypart.enabled) {
          if (daypart.startTime === dayparts[j].startTime) {
            return `Dayparts "${daypart.name}" and "${dayparts[j].name}" have the same start time`;
          }
        }
      }
    }

    return null;
  };

  const saveDaypartConfiguration = async () => {
    const validationError = validateDayparts();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = await getIdToken();
      
      const daypartsObject = dayparts.reduce((acc, daypart) => {
        acc[daypart.key] = daypart;
        return acc;
      }, {} as { [key: string]: Daypart });

      const configurationData: DaypartConfiguration = {
        enterpriseId,
        dayparts: daypartsObject,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        createdAt: configuration?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const response = await fetch(
        API_ENDPOINTS.enterpriseDayparts(enterpriseId),
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(configurationData)
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setConfiguration(configurationData);
          onSave?.(configurationData);
          alert('Daypart configuration saved successfully!');
        } else {
          setError(data.error || 'Failed to save configuration');
        }
      } else {
        // API endpoint might not exist yet - show success message
        setConfiguration(configurationData);
        onSave?.(configurationData);
        alert('Daypart configuration saved locally! API integration coming soon.');
      }
    } catch (err) {
      console.error('Error saving daypart configuration:', err);
      setError('Failed to save daypart configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="daypart-configuration loading">
        <div className="loading-spinner">Loading daypart configuration...</div>
      </div>
    );
  }

  return (
    <div className="daypart-configuration">
      <div className="daypart-header">
        <h3>Daypart Configuration</h3>
        <p>Configure operating periods for weather tracking and analytics</p>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">ERROR:</span>
          {error}
        </div>
      )}

      <div className="template-selector">
        <h4>Quick Templates</h4>
        <div className="template-buttons">
          {Object.entries(templates).map(([key, template]) => (
            <button
              key={key}
              type="button"
              className={`template-button ${selectedTemplate === key ? 'active' : ''}`}
              onClick={() => loadTemplate(key)}
            >
              {template.name}
            </button>
          ))}
        </div>
      </div>

      <div className="dayparts-editor">
        <div className="dayparts-header">
          <h4>Daypart Periods</h4>
          <button
            type="button"
            className="add-daypart-button"
            onClick={addCustomDaypart}
          >
            + Add Custom Period
          </button>
        </div>

        <div className="dayparts-list">
          {dayparts.map((daypart, index) => (
            <div key={daypart.key} className="daypart-item">
              <div className="daypart-controls">
                <div className="daypart-basic">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={daypart.name}
                      onChange={(e) => updateDaypart(index, 'name', e.target.value)}
                      placeholder="Daypart name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Start Time</label>
                    <input
                      type="time"
                      value={daypart.startTime}
                      onChange={(e) => updateDaypart(index, 'startTime', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>End Time</label>
                    <input
                      type="time"
                      value={daypart.endTime}
                      onChange={(e) => updateDaypart(index, 'endTime', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Color</label>
                    <input
                      type="color"
                      value={daypart.color}
                      onChange={(e) => updateDaypart(index, 'color', e.target.value)}
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={daypart.enabled}
                        onChange={(e) => updateDaypart(index, 'enabled', e.target.checked)}
                      />
                      Enabled
                    </label>
                  </div>

                  <div className="daypart-actions">
                    <button
                      type="button"
                      className="remove-button"
                      onClick={() => removeDaypart(index)}
                      title="Remove daypart"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="daypart-preview">
                  <div 
                    className="daypart-color-bar"
                    style={{ backgroundColor: daypart.color }}
                  >
                    <span className="daypart-time-range">
                      {daypart.startTime} - {daypart.endTime}
                    </span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Description (Optional)</label>
                <input
                  type="text"
                  value={daypart.description || ''}
                  onChange={(e) => updateDaypart(index, 'description', e.target.value)}
                  placeholder="Optional description for this daypart"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="daypart-actions">
        <button
          type="button"
          className="save-button"
          onClick={saveDaypartConfiguration}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      {dayparts.length > 0 && (
        <div className="daypart-preview-timeline">
          <h4>Daily Timeline Preview</h4>
          <div className="timeline">
            {dayparts.filter(dp => dp.enabled).map((daypart, index) => (
              <div
                key={daypart.key}
                className="timeline-segment"
                style={{ 
                  backgroundColor: daypart.color,
                  flex: 1
                }}
                title={`${daypart.name}: ${daypart.startTime} - ${daypart.endTime}`}
              >
                <span className="timeline-label">{daypart.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DaypartConfiguration;